'use strict';
//
// --- Shared RBAC Policy (usertypes + capabilities) ---
//
// This module is the SINGLE SOURCE OF TRUTH for:
//   - the role -> usertype hierarchy (user_types)
//   - the capability -> granting-roles map (capabilities)
//   - which roles confer admin status (admin_roles)
//   - capabilities granted to every authenticated user (always_capabilities)
//
// It is consumed by BOTH sides of the app:
//   - server.js (CommonJS `require`) feeds this straight into
//     `new RBACEngine({ ... })` (homesight-lib/lib/rbac.js) to build request
//     context (ctx.usertype / ctx.admin / ctx.capabilities) for API enforcement.
//   - src/auth.js (webpack ESM `import`) uses the derive* helpers below to
//     compute the SAME usertype/admin/capabilities client-side from the
//     Keycloak token, so the UI can gate nav/cards without waiting on the API.
//
// Written as CommonJS (module.exports) rather than `export`/`import` because
// server.js/lib/*.js run as plain Node CommonJS (no "type": "module" in
// package.json) while src/*.js is parsed as ESM by webpack. CommonJS is the
// one format both sides can consume: webpack accepts a `require()`'d/default
// import of a CJS module, but Node cannot `require()` an `export`-based file.
//
// --- What this file is NOT responsible for ---
// This module only governs the GLOBAL usertype/capability layer (nav items,
// feature gates, admin flag). It does NOT define per-data-source access
// restrictions. Those live in:
//   - config/db_sources.json        (rbac.access_rules — usertype gates on
//                                     raw `direct.*` DB sources, e.g. "super"-only)
//   - config/compound_sources.json  (rbac.filters — row-level provider/org
//                                     scoping on compound/derived sources)
//   - schemas/*                     (cached RBAC snapshots for sources that
//                                     are discovered at runtime via the Kafka
//                                     `data-sources` topic — see
//                                     homesight-lib/lib/cache.js — rather than
//                                     statically defined in this repo's config)
// See homesight-lib/docs/DATA_SOURCE_API_DOCUMENTATION.md for the full
// per-source-type RBAC shape reference.
//

const user_types = [
    { type: 'super', roles: ['super', 'super-admin'] },
    { type: 'developer', roles: ['developer', 'developer-admin'] },
    { type: 'devops', roles: ['devops', 'devops-admin'] },
    { type: 'ops', roles: ['ops', 'ops-admin'] },
    { type: 'org', roles: ['org', 'org-admin'] },
    { type: 'dashboard', roles: ['dashboard', 'dashboard-admin'] },
    { type: 'ml', roles: ['ml', 'ml-admin'] },
];

const capabilities = {
    'stats.view': ['super', 'super-admin', 'developer', 'developer-admin', 'devops', 'devops-admin', 'ops', 'ops-admin', 'org', 'org-admin', 'dashboard', 'dashboard-admin', 'ml', 'ml-admin'],
    'live.logs.read': ['super', 'super-admin', 'developer', 'developer-admin', 'devops', 'devops-admin', 'ops', 'ops-admin', 'ml', 'ml-admin'],
    'live.logs.mode.docker': ['super', 'super-admin', 'developer', 'developer-admin', 'devops', 'devops-admin', 'ops', 'ops-admin', 'ml', 'ml-admin'],
    'live.logs.mode.kafka': ['super', 'super-admin', 'developer', 'developer-admin', 'devops', 'devops-admin', 'ops', 'ops-admin'],
    'live.logs.mode.prefer_docker': ['ops', 'ops-admin', 'ml', 'ml-admin'],
    'live.logs.service_profile.ops': ['ops', 'ops-admin'],
    'live.logs.service_profile.ml': ['ml', 'ml-admin'],
    'live.logs.service_profile.all': ['super', 'super-admin', 'developer', 'developer-admin', 'devops', 'devops-admin'],
    'dashboard.custom.manage': ['super', 'super-admin', 'developer-admin', 'ops-admin', 'devops-admin'],
    'dashboard.json.edit': ['super', 'super-admin', 'ops-admin'],
    'feedback.submit': ['super', 'super-admin', 'developer', 'developer-admin', 'ops', 'ops-admin', 'devops', 'devops-admin'],
    'elm.sandbox.access': ['super', 'super-admin', 'developer', 'developer-admin', 'devops', 'devops-admin'],
    'elm.services.access': ['super', 'super-admin', 'developer', 'developer-admin', 'devops', 'devops-admin', 'ops', 'ops-admin'],
    'service.upgrade': ['super', 'super-admin', 'developer', 'developer-admin', 'devops', 'devops-admin'],
};

const admin_roles = ['super-admin', 'developer-admin', 'devops-admin', 'ops-admin', 'org-admin', 'dashboard-admin', 'ml-admin'];

const always_capabilities = ['stats.view'];

// --- Pure derivation helpers ---
// These mirror the private derivation logic inside homesight-lib/lib/rbac.js's
// RBACEngine (#normalize_roles / #derive_usertype / #derive_admin /
// #derive_capabilities) so that src/auth.js's client-side derivation produces
// results identical to the server's ctx.usertype/ctx.admin/ctx.capabilities.

function normalizeRoles(raw_roles) {
    if (!Array.isArray(raw_roles)) return [];
    return Array.from(new Set(
        raw_roles.map(role => String(role || '').trim().toLowerCase()).filter(Boolean)
    ));
}

function deriveUsertype(role_set) {
    for (const entry of user_types) {
        for (const role of entry.roles) {
            if (role_set.has(role)) {
                return entry.type;
            }
        }
    }
    return 'user';
}

function deriveAdmin(role_set) {
    for (const admin_role of admin_roles) {
        if (role_set.has(admin_role)) return true;
    }
    return false;
}

function deriveCapabilities(role_set) {
    const caps = new Set(always_capabilities);
    for (const [capability, granting_roles] of Object.entries(capabilities)) {
        if (!Array.isArray(granting_roles)) continue;
        for (const role of granting_roles) {
            if (role_set.has(role)) {
                caps.add(capability);
                break;
            }
        }
    }
    return Array.from(caps);
}

module.exports = {
    user_types,
    capabilities,
    admin_roles,
    always_capabilities,
    normalizeRoles,
    deriveUsertype,
    deriveAdmin,
    deriveCapabilities,
};
